import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { selectEmbeddedChatOpen, setEmbeddedChatOpen } from 'src/app/slice';
import {
  initA3CEmbeddedConfig,
  createEmbeddedChatContainer,
  setEmbeddedChatVisible,
  setCurrentFocusMode,
  handleEmbeddedChatResize,
} from 'src/commons/utils/a3cEmbeddedChat';
import { useChromeConfigContext } from 'src/providers/ChromeConfigContextProvider';

interface EmbeddedChatManagerProps {
  isFocusMode: boolean;
}

/**
 * Pure side-effect component that manages embedded chat lifecycle:
 * - Registers the bidirectional toggle callback on ChromeConfiguration
 * - Initializes A3CConfig and creates the chat container
 * - Syncs chat visibility with Redux state
 *
 * Renders nothing. Only mounted on desktop (non-mobile).
 */
export const EmbeddedChatManager: React.VFC<EmbeddedChatManagerProps> = ({
  isFocusMode,
}) => {
  const dispatch = useDispatch();
  const isEmbeddedChatOpen = useSelector(selectEmbeddedChatOpen);
  const chromeConfig = useChromeConfigContext();
  const selectedAgentIdentifier =
    chromeConfig?.a3cConfig?.embeddedChatConfig?.selectedAgentIdentifier;
  const panelMode = chromeConfig?.a3cConfig?.embeddedChatConfig?.panelMode;
  const defaultWidth = panelMode === 'compact' ? 475 : 675;

  // One-time setup: register callbacks, init A3CConfig, and create the container.
  // isFocusMode and dispatch are stable after page load, so this runs essentially once.
  useEffect(() => {
    setCurrentFocusMode(isFocusMode);
    initA3CEmbeddedConfig({
      closable: !isFocusMode,
      onStateChange: (isOpen) => {
        dispatch(setEmbeddedChatOpen(isOpen));
      },
    });
    createEmbeddedChatContainer(isFocusMode);

    const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
    // Skip if the page's AACChromeAPI bundle is not the latest version.
    if (
      typeof chromeConfiguration.setOnEmbeddedChatToggle === 'function' &&
      typeof chromeConfiguration.setOnEmbeddedChatResize === 'function'
    ) {
      chromeConfiguration.setOnEmbeddedChatToggle((isOpen: boolean) => {
        dispatch(setEmbeddedChatOpen(isOpen));
      });
      chromeConfiguration.setOnEmbeddedChatResize((width: number) => {
        handleEmbeddedChatResize(width, isFocusMode);
      });
      AACChromeAPI.setChromeConfiguration(chromeConfiguration);
    }
  }, [isFocusMode, dispatch]);

  // Update closable state and chat panel visibility when dependencies change.
  useEffect(() => {
    if (selectedAgentIdentifier) {
      const chromeConfiguration = AACChromeAPI.getChromeConfiguration();
      if (typeof chromeConfiguration.setEmbeddedChatClosable === 'function') {
        chromeConfiguration.setEmbeddedChatClosable(!isFocusMode);
        AACChromeAPI.setChromeConfiguration(chromeConfiguration);
      }
    }

    setEmbeddedChatVisible(
      !!selectedAgentIdentifier && isEmbeddedChatOpen,
      defaultWidth,
      isFocusMode,
    );
    // Notify A3ChatAssets of panel visibility change (P411687905)
    try {
      window.A3CConfig?.onVisibilityChange?.(
        !!selectedAgentIdentifier && isEmbeddedChatOpen,
      );
    } catch (e) {
      // Log so external-code failures are observable without breaking Chrome UI
      console.warn('A3CConfig.onVisibilityChange failed', e);
    }
  }, [isFocusMode, selectedAgentIdentifier, isEmbeddedChatOpen]);

  return null;
};
